import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

export default function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    setPosition({ x: e.clientX, y: e.clientY });
    if (!isVisible) setIsVisible(true);
  }, [isVisible]);

  const handleMouseEnterLink = useCallback(() => setIsHovering(true), []);
  const handleMouseLeaveLink = useCallback(() => setIsHovering(false), []);
  const handleMouseDown = useCallback(() => setIsClicking(true), []);
  const handleMouseUp = useCallback(() => setIsClicking(false), []);
  const handleMouseLeaveWindow = useCallback(() => setIsVisible(false), []);
  const handleMouseEnterWindow = useCallback(() => setIsVisible(true), []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeaveWindow);
    document.addEventListener('mouseenter', handleMouseEnterWindow);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    const interactiveEls = document.querySelectorAll('a, button, [role="button"], input, textarea, select, [data-cursor-hover]');
    interactiveEls.forEach((el) => {
      el.addEventListener('mouseenter', handleMouseEnterLink);
      el.addEventListener('mouseleave', handleMouseLeaveLink);
    });

    const observer = new MutationObserver(() => {
      const els = document.querySelectorAll('a, button, [role="button"], input, textarea, select, [data-cursor-hover]');
      els.forEach((el) => {
        el.removeEventListener('mouseenter', handleMouseEnterLink);
        el.removeEventListener('mouseleave', handleMouseLeaveLink);
        el.addEventListener('mouseenter', handleMouseEnterLink);
        el.addEventListener('mouseleave', handleMouseLeaveLink);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeaveWindow);
      document.removeEventListener('mouseenter', handleMouseEnterWindow);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      observer.disconnect();
      interactiveEls.forEach((el) => {
        el.removeEventListener('mouseenter', handleMouseEnterLink);
        el.removeEventListener('mouseleave', handleMouseLeaveLink);
      });
    };
  }, [handleMouseMove, handleMouseEnterLink, handleMouseLeaveLink, handleMouseDown, handleMouseUp, handleMouseLeaveWindow, handleMouseEnterWindow]);

  return (
    <motion.div
      className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference"
      animate={{
        x: position.x,
        y: position.y,
        opacity: isVisible ? 1 : 0,
      }}
      transition={{
        x: { type: 'spring', stiffness: 500, damping: 28, mass: 0.5 },
        y: { type: 'spring', stiffness: 500, damping: 28, mass: 0.5 },
        opacity: { duration: 0.2 },
      }}
    >
      <motion.div
        className="flex items-center justify-center"
        animate={{
          width: isClicking ? 28 : isHovering ? 56 : 32,
          height: isClicking ? 28 : isHovering ? 56 : 32,
        }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      >
        <motion.div
          className="rounded-full border border-white bg-white/10"
          animate={{
            width: isClicking ? 28 : isHovering ? 56 : 32,
            height: isClicking ? 28 : isHovering ? 56 : 32,
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          {isHovering && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="w-full h-full flex items-center justify-center"
            >
              <span className="text-[10px] text-white font-medium tracking-wider whitespace-nowrap">VER</span>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}